import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { ArrowLeft, Mail, Shield, Users, FileText, Briefcase, TrendingUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";

export function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const { user, userType } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();

  // Define slides with translation keys
  const slides = [
    {
      badge: 'فرص استثمارية',
      title: 'و أخيراً الاستثمار المباشر في الثروة المعرفية',
      description: 'بأرباح قوية بعيداً عن الفوائد البنكية! انضم إلينا الآن وابدأ بيع مشاريعك أو استثمر في أفكار مبتكرة.',
      leftTitle: '', // Not used since we have static video
      leftSubtitle: '', // Not used since we have static video
      image: "https://econ-pedia.com/wp-content/uploads/2023/12/stock-market.jpg",
      isVideo: false, // Changed to false since it's an image now
    },
    {
      badge: t('public.hero.slide2.badge'),
      title: t('public.hero.slide2.title'),
      description: t('public.hero.slide2.description'),
      leftTitle: t('public.hero.slide2.leftTitle'),
      leftSubtitle: t('public.hero.slide2.leftSubtitle'),
      image: "https://www.atmacss.com/wp-content/uploads/2024/08/strategic-partnership.jpg",
      isVideo: false,
    },
    {
      badge: t('public.hero.slide3.badge'),
      title: t('public.hero.slide3.title'),
      description: t('public.hero.slide3.description'),
      leftTitle: t('public.hero.slide3.leftTitle'),
      leftSubtitle: t('public.hero.slide3.leftSubtitle'),
      image: "https://mz-mz.net/wp-content/up/2022/02/WhatsApp-Image-2022-02-13-at-4.39.49-PM.jpeg",
      isVideo: false,
    },
  ];

  // Define quick links with translation keys
  const quickLinks = [
    { icon: Shield, title: t('public.quickLinks.grants.title'), label: t('public.quickLinks.grants.label') },
    { icon: Users, title: t('public.quickLinks.leaders.title'), label: t('public.quickLinks.leaders.label') },
    { icon: FileText, title: t('public.quickLinks.applications.title'), label: t('public.quickLinks.applications.label') },
    { icon: Briefcase, title: t('public.quickLinks.investment.title'), label: t('public.quickLinks.investment.label') },
    { icon: TrendingUp, title: t('public.quickLinks.platform.title'), label: t('public.quickLinks.platform.label') },
  ];

  const goToSlide = useCallback((index: number, dir: 'next' | 'prev' = 'next') => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 800);
  }, [isAnimating, t]);

  const nextSlide = useCallback(() => {
    const next = (currentSlide + 1) % slides.length;
    goToSlide(next, 'next');
  }, [currentSlide, goToSlide, slides.length]);

  const prevSlide = useCallback(() => {
    const prev = (currentSlide - 1 + slides.length) % slides.length;
    goToSlide(prev, 'prev');
  }, [currentSlide, goToSlide, slides.length]);

  useEffect(() => {
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const slide = slides[currentSlide];

  // Extract video ID from YouTube URL
  const extractVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Get video ID for current slide if it's a video
  const videoId = slide.isVideo ? extractVideoId(slide.image) : null;

  return (
    <section className="relative" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Main Hero */}
      <div className="relative min-h-[600px] sm:min-h-[520px] md:h-[480px] overflow-hidden bg-card">
        <div className="absolute inset-0 flex flex-col sm:flex-row">

          {/* Left Panel - Static Video */}
          <div className="relative w-full sm:w-[30%] hidden lg:block overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center p-4">
            <div className="w-full h-full flex items-center justify-center">
              {/* YouTube Video */}
              <div className="relative pt-[56.25%] h-full w-full"> {/* 16:9 Aspect Ratio */}
                <iframe
                  src="https://www.youtube.com/embed/doMxuu9p0a4?si=ydMZUeKxGBFUcFfM&autoplay=1"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* Center Panel - Main Content */}
          <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 md:px-12 lg:px-7 relative z-10">
            <div className="max-w-full sm:max-w-xl">
              {/* Badge with Image - Positioned on the far left of center panel */}
              <div
                className={`flex items-center justify-start mb-4 sm:mb-6 transition-all duration-500 ${isAnimating ? 'opacity-0 -translate-y-4' : 'opacity-100 translate-y-0'
                  }`}
              >
                <div className="flex items-center w-full">
                  {/* Image container - positioned on the far left */}
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg bg-primary/10 flex items-center justify-center border-2 border-primary/20 flex-shrink-0">
                    {/* Placeholder for image - replace with actual image component */}
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-primary rounded-md flex items-center justify-center">
                      <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary-foreground" />
                    </div>
                  </div>
                  <span className="px-3 sm:px-5 py-1 sm:py-2 bg-primary text-primary-foreground text-xs sm:text-sm font-semibold inline-block rounded-r-lg mr-2 sm:mr-4">
                    {slide.badge}
                  </span>
                </div>
              </div>

              {/* Title */}
              <h1
                className={`text-2xl sm:text-xl md:text-4xl lg:text-2xl font-bold text-foreground leading-tight mb-4 sm:mb-6 transition-all duration-600 delay-100 ${isAnimating ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0'
                  }`}
              >
                {slide.title}
              </h1>

              {/* Description */}
              <p
                className={`text-sm sm:text-base md:text-md text-muted-foreground leading-relaxed mb-6 sm:mb-8 transition-all duration-600 delay-200 whitespace-pre-line ${isAnimating ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0'
                  }`}
              >
                {slide.description}
              </p>

              {/* Video/Image container for mobile - only for mobile */}
              <div className="flex sm:hidden justify-center items-center mb-6">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className={`
                      w-auto
                      rounded-lg
                      max-w-[150px] 
                      h-auto 
                      object-contain
                      transition-all duration-700 ease-out
                      ${isAnimating ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}
                    `}
                />
              </div>

              {/* Buttons */}
              <div
                className={`flex flex-col sm:flex-row flex-wrap gap-3 mb-6 sm:mb-10 transition-all duration-600 delay-300 ${isAnimating ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0'
                  }`}
              >
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 sm:px-8 group w-full sm:w-auto"
                  onClick={() => {
                    // Navigate to project holder registration or projects if logged in
                    if (user) {
                      navigate('/projects');
                    } else {
                      navigate('/project-holder-register');
                    }
                  }}
                >
                  {user ? (language === "ar" ? "تصفح المشاريع" : "Browse Projects") : "سجل معنا"}
                  <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 gap-2 w-full sm:w-auto"
                  onClick={() => {
                    // Navigate to investor registration or project sales if logged in
                    if (user) {
                      navigate('/project-sales');
                    } else {
                      navigate('/investor-register');
                    }
                  }}
                >
                  <Mail className="w-4 h-4" />
                  {user ? (language === "ar" ? "مشاريع للبيع" : "Projects for Sale") : "استثمر معنا"}
                </Button>

              </div>

              {/* Navigation Dots */}
              <div
                className={`flex items-center gap-2 transition-all duration-600 delay-400 ${isAnimating ? 'opacity-0' : 'opacity-100'
                  }`}
              >
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index, index > currentSlide ? 'next' : 'prev')}
                    className={`h-2 sm:h-3 rounded-full transition-all duration-400 ${index === currentSlide
                      ? 'bg-primary w-6 sm:w-8'
                      : 'bg-muted-foreground/30 w-2 sm:w-3 hover:bg-muted-foreground/50'
                      }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:justify-center sm:items-center">
            <img
              src={slide.image}
              alt={slide.title}
              className={`
                  w-auto
                  rounded-lg
                  max-w-[200px] sm:max-w-[300px] md:max-w-md 
                  h-auto 
                  object-contain
                  transition-all duration-700 ease-out
                  ${isAnimating ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}
                `}
            />
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          disabled={isAnimating}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 z-20 disabled:opacity-50"
          aria-label={t('public.hero.prevSlide')}
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <button
          onClick={nextSlide}
          disabled={isAnimating}
          className="absolute right-2 sm:right-1 md:right-[25%] top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-card/90 backdrop-blur-sm flex items-center justify-center shadow-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 z-20 disabled:opacity-50"
          aria-label={t('public.hero.nextSlide')}
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>


    </section>
  );
}